import React, {FC, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {Alert, App, Form, Modal, Segmented, Select, Skeleton, Typography} from "antd";
import {useTranslation} from "react-i18next";
import {AuthContext} from "../../Contexts/AuthContext";
import {authorizedFetchOptions} from "../../api/authorization";
import {readDownloadErrorMessage} from "../../downloadFile";
import {useParticipantPayerOptionsQuery} from "../../GraphQL/generated/graphql";
import {SubmitErrorAlert} from "../publicRegistration/FormFeedback";
import {PayerFields} from "../publicRegistration/PayerFields";
import {SingleRegistrationForm} from "../publicRegistration/SingleRegistrationForm";
import {buildInitialFormValues, normalizePublicRegistrationLanguage} from "../publicRegistration/publicRegistrationModel";
import {
    buildRegistrationCopy,
    RegistrationCopyProvider,
    usePublicRegistrationLabels
} from "../publicRegistration/registrationCopy";
import {buildPublicRegistrationSubmitPayload} from "../publicRegistration/registrationPayload";
import {localizeSubmitErrorMessage} from "../publicRegistration/submitErrors";
import type {
    PublicEventFormResponse,
    PublicRegistrationFormValues,
    PublicRegistrationResponse
} from "../publicRegistration/types";
import {useRegistrationFormState} from "../publicRegistration/useRegistrationFormState";

export type OrganizerRegistrationParticipant = {
    id: number;
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    phoneNumber?: string | null;
    company?: string | null;
    linkedInUrl?: string | null;
};

type PayerMode = "new" | "existing";

// Organizer registration with the public form's sections and rules. The server applies the same validation
// (see EventRegistrationFormController); organizers skip GDPR consent and proof upload and may pick an
// existing payer. The participant still confirms the email address before the offer is sent.
export const OrganizerRegistrationModal: FC<{
    eventId: number;
    open: boolean;
    participant?: OrganizerRegistrationParticipant;
    onClose: () => void;
    onRegistered: (response: PublicRegistrationResponse) => void;
}> = ({eventId, open, participant, onClose, onRegistered}) => {
    const {t, i18n} = useTranslation();
    const {message} = App.useApp();
    const {auth} = useContext(AuthContext);
    const language = normalizePublicRegistrationLanguage(i18n.language);
    const labels = usePublicRegistrationLabels(language);
    const copy = useMemo(() => buildRegistrationCopy(labels, language), [labels, language]);
    const [form] = Form.useForm<PublicRegistrationFormValues>();
    const watchedValues = Form.useWatch([], form) as PublicRegistrationFormValues | undefined;
    const [eventForm, setEventForm] = useState<PublicEventFormResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [payerMode, setPayerMode] = useState<PayerMode>("new");
    const [payerId, setPayerId] = useState<number>();
    const [payerSearch, setPayerSearch] = useState("");
    const payerOptionsQuery = useParticipantPayerOptionsQuery({
        variables: {search: payerSearch.trim()},
        skip: !open || payerMode !== "existing",
        fetchPolicy: "cache-and-network"
    });
    const state = useRegistrationFormState(eventForm, form, watchedValues);
    const formAvailable = !!eventForm?.template && !eventForm.registrationUnavailableReason;

    const loadForm = useCallback(async (signal: AbortSignal) => {
        setLoading(true);
        setLoadError(null);
        try {
            const response = await fetch(
                `/api/events/${eventId}/registration-form`,
                authorizedFetchOptions(auth.token, {headers: {Accept: "application/json"}, signal}));
            if (!response.ok) {
                throw new Error(await readDownloadErrorMessage(response, ""));
            }

            const payload = await response.json() as PublicEventFormResponse;
            setEventForm(payload);
            form.resetFields();
            form.setFieldsValue({
                ...buildInitialFormValues(payload),
                firstName: participant?.firstName ?? undefined,
                lastName: participant?.lastName ?? undefined,
                email: participant?.email ?? undefined,
                phoneNumber: participant?.phoneNumber ?? undefined,
                company: participant?.company ?? undefined,
                linkedInUrl: participant?.linkedInUrl ?? undefined
            });
        } catch (error) {
            if ((error as DOMException).name !== "AbortError") {
                setLoadError(t("organizer-registration.load-failed", {
                    defaultValue: "The registration form could not be loaded."
                }));
            }
        } finally {
            setLoading(false);
        }
    }, [auth.token, eventId, form, participant, t]);

    useEffect(() => {
        if (!open) {
            return;
        }

        setSubmitError(null);
        setPayerMode("new");
        setPayerId(undefined);
        const controller = new AbortController();
        void loadForm(controller.signal);
        return () => controller.abort();
    }, [loadForm, open]);

    const submit = async () => {
        if (!eventForm || !formAvailable) {
            return;
        }

        setSubmitError(null);
        if (payerMode === "existing" && !payerId) {
            setSubmitError(t("organizer-registration.payer-required", {defaultValue: "Select a payer."}));
            return;
        }

        let values: PublicRegistrationFormValues;
        try {
            values = await form.validateFields();
        } catch {
            setSubmitError(labels.validationErrorSummary);
            return;
        }

        setSubmitting(true);
        try {
            const payload = {
                ...buildPublicRegistrationSubmitPayload(values, eventForm),
                gdprConsentAccepted: false,
                participantTypeProofFileId: undefined,
                payerId: payerMode === "existing" ? payerId : undefined,
                participantId: participant?.id
            };
            const response = await fetch(
                `/api/events/${eventId}/registrations`,
                authorizedFetchOptions(auth.token, {
                    method: "POST",
                    headers: {Accept: "application/json", "Content-Type": "application/json"},
                    body: JSON.stringify(payload)
                }));
            if (!response.ok) {
                throw new Error(await readDownloadErrorMessage(response, labels.submitError));
            }

            const result = await response.json() as PublicRegistrationResponse;
            message.success(t("organizer-registration.success", {
                defaultValue: "The participant is registered. An email to confirm the address has been sent."
            }));
            onRegistered(result);
        } catch (error) {
            setSubmitError(error instanceof Error
                ? localizeSubmitErrorMessage(error.message, labels, language)
                : labels.submitError);
        } finally {
            setSubmitting(false);
        }
    };

    const payerContent = (
        <>
            <Segmented<PayerMode>
                value={payerMode}
                onChange={setPayerMode}
                options={[
                    {value: "new", label: t("organizer-registration.payer-mode-new", {defaultValue: "New payer"})},
                    {value: "existing", label: t("organizer-registration.payer-mode-existing", {defaultValue: "Existing payer"})}
                ]}
                style={{marginBottom: 16}}
            />
            {payerMode === "existing" ? (
                <Form.Item label={t("select-payer", {defaultValue: "Select payer"})} required>
                    <Select
                        showSearch
                        allowClear
                        filterOption={false}
                        value={payerId}
                        loading={payerOptionsQuery.loading}
                        placeholder={t("select-payer-placeholder", {defaultValue: "Search by name, email, or tax ID"})}
                        onSearch={setPayerSearch}
                        onChange={value => setPayerId(value ?? undefined)}
                        options={(payerOptionsQuery.data?.payers?.items ?? []).map(payer => ({
                            value: payer.id,
                            label: [payer.name?.trim() || `#${payer.id}`, payer.email?.trim(), payer.taxId?.trim()]
                                .filter(Boolean)
                                .join(" · ")
                        }))}
                    />
                </Form.Item>
            ) : <PayerFields payerType={state.payerType}/>}
        </>
    );

    return (
        <Modal
            open={open}
            title={t("organizer-registration.title", {defaultValue: "Register participant"})}
            okText={t("organizer-registration.submit", {defaultValue: "Register"})}
            onOk={() => void submit()}
            okButtonProps={{loading: submitting, disabled: !formAvailable}}
            onCancel={onClose}
            width={880}
            destroyOnHidden
        >
            {loading ? <Skeleton active paragraph={{rows: 8}}/> : null}
            {loadError ? <Alert type="error" showIcon message={loadError}/> : null}
            {!loading && eventForm && !formAvailable ? (
                <Alert
                    type="warning"
                    showIcon
                    message={t("organizer-registration.unavailable", {
                        defaultValue: "Registration is not possible: the event is completed or has no active registration form."
                    })}
                />
            ) : null}
            {!loading && eventForm && formAvailable ? (
                <RegistrationCopyProvider value={copy}>
                    <Typography.Paragraph type="secondary">
                        {t("organizer-registration.verification-note", {
                            defaultValue: "The participant receives an email to confirm the address; the offer is sent after confirmation, the same as for public registration."
                        })}
                    </Typography.Paragraph>
                    <Form form={form} layout="vertical" onValuesChange={() => submitError && setSubmitError(null)}>
                        <SingleRegistrationForm
                            eventForm={eventForm}
                            payerType={state.payerType}
                            requiresParticipationFee={state.requiresParticipationFee}
                            registrationOptions={state.availableRegistrationOptions}
                            showProofField={false}
                            proofUploading={false}
                            onProofUpload={() => undefined}
                            showParticipationPayment={state.hasParticipationOfferItems}
                            participationMethods={state.participationMethods}
                            reserveAccommodation={state.reserveAccommodation}
                            selectedRoom={state.selectedRoom}
                            accommodationEstimate={state.accommodationEstimate}
                            accommodationMethods={state.accommodationMethods}
                            submitting={submitting}
                            submitErrorAlert={<SubmitErrorAlert message={submitError}/>}
                            payerContent={payerContent}
                            organizerMode
                        />
                    </Form>
                </RegistrationCopyProvider>
            ) : null}
        </Modal>
    );
};
